import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, RotateCcw, Wand2, Percent, Home, Coins, FileDown, ChevronDown, Landmark, Receipt, TrendingUp, Layers, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { exportPianoPdf } from "@/lib/exportPianoPdf";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtPct  = (n) => `${(Number(n) || 0).toFixed(2)}%`;
const fmtNum  = (n, d = 0) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: d }).format(Number(n) || 0);
const uid     = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;
const matPrezzo = (m) => Number(m?.prezzo) > 0 ? Number(m.prezzo) : Number(m?.prezzo_a) || 0;
const matQty    = (m) => (m?.quantita === undefined || m?.quantita === null) ? 1 : Number(m.quantita);

const PCT_REF_LABEL = { ricavi: "Ricavi", proposta: "Proposta", manodopera: "Manodopera", rendita: "Rendita" };

const costLineAmount = (cl, econ, k) => {
  const val = Number(cl?.[k]) || 0;
  if ((cl?.mode || "fixed") !== "percent") return val;
  const ref =
    cl.pct_of === "ricavi"     ? (Number(econ.ricavi?.[k]) || 0) :
    cl.pct_of === "proposta"   ? (Number(econ.proposta?.[k]) || 0) :
    cl.pct_of === "manodopera" ? (Number(econ.manodopera?.[k]) || 0) :
    cl.pct_of === "rendita"    ? (Number(econ.rendita_catastale) || 0) : 0;
  return (val / 100) * ref;
};

const SCENARIOS = [
  { key: "peggiore", label: "PEGGIORE", color: "text-rose-600",    bg: "bg-rose-50" },
  { key: "medio",    label: "MEDIO",    color: "text-amber-600",   bg: "bg-amber-50" },
  { key: "migliore", label: "MIGLIORE", color: "text-emerald-600", bg: "bg-emerald-50" },
];
const emptyTriple = () => ({ peggiore: 0, medio: 0, migliore: 0 });

const tasseRegistro = (rendita, tipo, propostaMedio) => {
  const r = Number(rendita) || 0;
  const p = Number(propostaMedio) || 0;
  if (tipo === "1casa") return r * 115.5 * 0.02;
  if (tipo === "2casa") return r * 126 * 0.09;
  if (tipo === "srl")   return 0.09 * p;
  return 0;
};

const totaleMateriali = (materials) => (materials || []).reduce((acc, m) => acc + matQty(m) * matPrezzo(m), 0);

const totaleCostiPerScenario = (cost_lines, econ, k) =>
  (cost_lines || []).reduce((acc, l) => acc + costLineAmount(l, econ, k), 0);

const computeDerived = (econ, property) => {
  const mq = Number(property?.mq) || 0;
  const tipo = econ.tipo_acquirente || "1casa";
  const propostaMedio = Number(econ.proposta?.medio) || 0;
  const tasse = tasseRegistro(econ.rendita_catastale, tipo, propostaMedio);
  const imprPct = (Number(econ.imprevisti_pct) || 0) / 100;
  const sost = (Number(econ.sostitutiva_pct) || 0) / 100;
  const pubMult = 1 + (Number(econ.pubblicita_pct) || 0) / 100;
  const esp = Number(econ.esposizione) || 0;
  const matTot = totaleMateriali(econ.materials);

  const costTotPerScenario = {
    peggiore: totaleCostiPerScenario(econ.cost_lines, econ, "peggiore"),
    medio:    totaleCostiPerScenario(econ.cost_lines, econ, "medio"),
    migliore: totaleCostiPerScenario(econ.cost_lines, econ, "migliore"),
  };

  const totFor = (k) => {
    const prop = Number(econ.proposta?.[k]) || 0;
    const manod = Number(econ.manodopera?.[k]) || 0;
    return prop + manod + costTotPerScenario[k] + manod * imprPct + tasse + matTot;
  };
  const totSpese = { peggiore: totFor("peggiore"), medio: totFor("medio"), migliore: totFor("migliore") };
  const ric = econ.ricavi || {};
  const utileLordo = {
    peggiore: (Number(ric.peggiore) || 0) - totSpese.peggiore,
    medio:    (Number(ric.medio) || 0)    - totSpese.medio,
    migliore: (Number(ric.migliore) || 0) - totSpese.migliore,
  };
  const pct = (n, d) => d ? (n / d) * 100 : 0;
  const impostaSost = {
    peggiore: Math.max(utileLordo.peggiore, 0) * sost,
    medio:    Math.max(utileLordo.medio, 0)    * sost,
    migliore: Math.max(utileLordo.migliore, 0) * sost,
  };
  const utileNettoSenzaResid = {
    peggiore: utileLordo.peggiore - impostaSost.peggiore,
    medio:    utileLordo.medio    - impostaSost.medio,
    migliore: utileLordo.migliore - impostaSost.migliore,
  };
  const roi = { peggiore: pct(utileLordo.peggiore, totSpese.peggiore), medio: pct(utileLordo.medio, totSpese.medio), migliore: pct(utileLordo.migliore, totSpese.migliore) };
  const roe = { peggiore: pct(utileLordo.peggiore, esp), medio: pct(utileLordo.medio, esp), migliore: pct(utileLordo.migliore, esp) };
  const pubblicita = {
    peggiore: (Number(ric.peggiore) || 0) * pubMult,
    medio:    (Number(ric.medio) || 0)    * pubMult,
    migliore: (Number(ric.migliore) || 0) * pubMult,
  };

  return { tasse, matTot, costTot: costTotPerScenario, totSpese, utileLordo, roi, roe, impostaSost, utileNettoSenzaResid, pubblicita };
};

const RENO_HINTS = {
  STRONG: { peggiore: 400, medio: 375, migliore: 350, desc: "Rifacimento totale (impianti + infissi)" },
  MEDIUM: { peggiore: 250, medio: 225, migliore: 200, desc: "Si salva qualcosa fra impianti e infissi" },
  LIGHT:  { peggiore: 150, medio: 125, migliore: 100, desc: "Si salvano sia infissi sia impianti" },
};

// Calcolatore inverso: dato il ROI target ricava la proposta d'acquisto massima
// che produce quel ROI nello scenario MEDIO. Formula:
//   ROI = utile / spese = (ricavi - spese) / spese
//   => spese_max = ricavi / (1 + ROI/100)
//   spese = proposta × (1 + K) + F
//     K = Σ(pct/100 per cost_lines con pct_of="proposta") + (0.09 se tipo=srl)
//     F = manodopera + imprevisti + materiali + tasse_non_proposta + Σ(cost_lines non-proposta)
//   => proposta_max = (spese_max - F) / (1 + K)
const reverseCalc = (econ, roiTargetPct, scenarioKey = "medio") => {
  const roi = Number(roiTargetPct) || 0;
  const k = scenarioKey || "medio";
  const ricS = Number(econ.ricavi?.[k]) || 0;
  if (ricS <= 0) return { ok: false, reason: "Definisci prima i ricavi per lo scenario selezionato" };
  const totSpeseTarget = ricS / (1 + roi / 100);

  const manS = Number(econ.manodopera?.[k]) || 0;
  const impPct = Number(econ.imprevisti_pct) || 0;
  const imprevisti = manS * impPct / 100;
  const matTot = totaleMateriali(econ.materials);
  const rendita = Number(econ.rendita_catastale) || 0;
  const tipo = econ.tipo_acquirente || "1casa";
  const tasseNonProp =
    tipo === "1casa" ? rendita * 115.5 * 0.02 :
    tipo === "2casa" ? rendita * 126   * 0.09 : 0;

  let F = manS + imprevisti + matTot + tasseNonProp;
  let K = tipo === "srl" ? 0.09 : 0;
  for (const l of (econ.cost_lines || [])) {
    const mode = l.mode || "fixed";
    const v = Number(l[k]) || 0;
    if (mode === "percent") {
      if (l.pct_of === "proposta")        K += v / 100;
      else if (l.pct_of === "ricavi")     F += (v / 100) * ricS;
      else if (l.pct_of === "manodopera") F += (v / 100) * manS;
      else if (l.pct_of === "rendita")    F += (v / 100) * rendita;
    } else {
      F += v;
    }
  }
  const propostaMax = (totSpeseTarget - F) / (1 + K);
  return {
    ok: propostaMax > 0,
    proposta: propostaMax,
    totSpeseTarget, F, K,
    reason: propostaMax <= 0 ? "Con i costi attuali il ROI target non è raggiungibile" : null,
  };
};

// Calcolatore inverso: dato ROI target → proposta max per uno scenario scelto
const ReverseCalculator = ({ econ, setEcon }) => {
  const [target, setTarget] = useState(25);
  const [scenario, setScenario] = useState("medio");
  const result = useMemo(() => reverseCalc(econ, target, scenario), [econ, target, scenario]);
  const apply = () => {
    if (!result.ok) return;
    const v = Math.max(0, Math.round(result.proposta));
    setEcon(s => ({ ...s, proposta: { ...(s.proposta || emptyTriple()), [scenario]: v } }));
    const scenLabel = SCENARIOS.find(x => x.key === scenario)?.label || scenario;
    toast.success(`Proposta ${scenLabel} = ${fmtEuro(v)} (ROI target ${target}%)`);
  };
  return (
    <div className="rounded-2xl p-5 bg-gradient-to-br from-purple-500 via-purple-600 to-purple-800 text-white shadow-lg flex flex-col" data-testid="piano-reverse-calc">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4" />
        <span className="text-[10px] font-bold uppercase tracking-[0.24em] opacity-90">Calcolatore inverso</span>
      </div>
      <h3 className="font-display font-bold text-base mb-2 leading-tight">Voglio un ROI del…</h3>
      <div className="flex items-center gap-2 mb-2">
        <input
          type="number" step="1"
          value={target}
          onChange={e => setTarget(e.target.value === "" ? 0 : Number(e.target.value))}
          className="w-24 h-10 rounded-lg bg-white/95 text-slate-900 font-mono-num font-bold text-lg px-3 focus:outline-none focus:ring-2 focus:ring-white/60"
          data-testid="piano-roi-target"
        />
        <span className="font-bold text-lg">%</span>
      </div>
      <div className="mb-3">
        <div className="text-[9px] uppercase tracking-widest text-purple-100 mb-1">Scenario</div>
        <Select value={scenario} onValueChange={setScenario}>
          <SelectTrigger className="h-8 text-xs bg-white/95 text-slate-900 border-white/40" data-testid="piano-reverse-scenario"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="peggiore">Peggiore</SelectItem>
            <SelectItem value="medio">Medio</SelectItem>
            <SelectItem value="migliore">Migliore</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="text-[9px] uppercase tracking-widest text-purple-100 mb-1">Offerta massima</div>
      {result.ok ? (
        <div className="font-mono-num font-bold text-2xl mb-3" data-testid="piano-proposta-max">{fmtEuro(result.proposta)}</div>
      ) : (
        <div className="text-[11px] text-purple-100/90 py-2 mb-1 leading-tight" data-testid="piano-reverse-warn">{result.reason || "—"}</div>
      )}
      <Button
        variant="outline" size="sm" onClick={apply} disabled={!result.ok}
        className="w-full rounded-full bg-white/90 hover:bg-white text-purple-900 border-white/50 mt-auto"
        data-testid="piano-reverse-apply"
      >
        Applica allo scenario
      </Button>
    </div>
  );
};

const NumCell = ({ value, onChange, testId, className = "" }) => (
  <Input
    type="number" step="0.01" value={value ?? 0}
    onChange={e => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    className={`h-8 text-right font-mono-num text-xs px-2 ${className}`}
    data-testid={testId}
  />
);

// ─────────────────────────────────────────────────────────────────────
// Componente riutilizzabile: intestazione di sezione ben visibile
// ─────────────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, dotColor = "bg-[#0A84FF]", right, testId }) => (
  <div className="flex items-center gap-2.5 mb-2" data-testid={testId}>
    <span className={`w-2 h-2 rounded-full ${dotColor} shadow-sm`} />
    {Icon && <Icon className="w-4 h-4 text-slate-700" strokeWidth={2.2} />}
    <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-800">{title}</h3>
    <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
    {right}
  </div>
);

// Riga a 3 colonne scenario (readonly), con label e valori formattati come euro/pct.
const ScenarioRow = ({ label, values, format = fmtEuro, valueClass = "text-slate-800", labelClass = "text-slate-700", boldTotal = false, rowBg = "" }) => (
  <tr className={rowBg}>
    <td className={`px-2 py-1.5 ${labelClass} ${boldTotal ? "font-bold" : ""}`}>{label}</td>
    {SCENARIOS.map(s => (
      <td key={s.key} className={`text-right px-2 py-1.5 font-mono-num ${boldTotal ? "font-bold " + SCENARIOS.find(x=>x.key===s.key).color : valueClass}`}>
        {format(values[s.key])}
      </td>
    ))}
    <td style={{ width: 32 }}></td>
  </tr>
);

// ─────────────────────────────────────────────────────────────────────
// Toolbar impostazioni piano (usata sia in Scenari sia in Costi)
// ─────────────────────────────────────────────────────────────────────
const SettingsToolbar = ({ econ, setEcon }) => (
  <div className="flex flex-wrap items-center gap-2 bg-slate-50 border rounded-lg p-2 text-xs mb-4">
    <div className="flex items-center gap-1">
      <span className="text-slate-500">Tipo acquirente:</span>
      <Select value={econ.tipo_acquirente || "1casa"} onValueChange={v => setEcon(s => ({ ...s, tipo_acquirente: v }))}>
        <SelectTrigger className="h-7 w-32 text-xs" data-testid="piano-tipo-acquirente"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="1casa">1° casa</SelectItem>
          <SelectItem value="2casa">2° casa</SelectItem>
          <SelectItem value="srl">SRL (9%)</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <span className="text-[10px] text-slate-400 italic">Determina il calcolo delle tasse di registro</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// TAB 1 · Scenari (riepilogo con sezioni + accordion su Costi/Materiali)
// ─────────────────────────────────────────────────────────────────────
const ScenariiTab = ({ econ, setEcon, derived, property }) => {
  const [openCosts, setOpenCosts] = useState(false);
  const [openMats, setOpenMats] = useState(false);
  const setTriple = (field, k, v) => setEcon(s => ({ ...s, [field]: { ...(s[field] || emptyTriple()), [k]: Number(v) || 0 } }));

  const HEAD = (
    <thead className="bg-slate-100">
      <tr>
        <th className="text-left px-2 py-2 uppercase tracking-wider text-[10px] text-slate-600">Voce</th>
        {SCENARIOS.map(s => (
          <th key={s.key} className={`text-right px-2 py-2 uppercase tracking-wider text-[10px] font-bold ${s.color}`} style={{ width: 130 }}>{s.label}</th>
        ))}
        <th style={{ width: 32 }}></th>
      </tr>
    </thead>
  );

  const Accordion = ({ open, onToggle, title, total, testIdKey, icon, dotColor }) => (
    <button type="button" onClick={onToggle}
      className="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors text-left"
      data-testid={`piano-accord-${testIdKey}`}>
      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
      <span className={`w-2 h-2 rounded-full ${dotColor} shadow-sm`} />
      {icon}
      <span className="font-display font-bold text-sm uppercase tracking-[0.12em] text-slate-800">{title}</span>
      <span className="flex-1" />
      <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-2">Totale</span>
      <span className="font-mono-num font-bold text-slate-900 text-sm">{fmtEuro(total)}</span>
    </button>
  );

  return (
    <div className="space-y-5">
      <SettingsToolbar econ={econ} setEcon={setEcon} />

      {/* Sezione 1 · Ns. Proposta d'acquisto (hero card) + Calcolatore inverso */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-3">
          <div className="rounded-2xl p-5 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-lg" data-testid="piano-proposta-hero">
            <div className="flex items-center gap-2 mb-1">
              <Home className="w-4 h-4" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] opacity-90">Da definire per primo</span>
            </div>
            <h3 className="font-display font-bold text-xl mb-1">Ns. Proposta d'acquisto</h3>
            <p className="text-[11px] text-blue-100 mb-4 max-w-2xl">Il prezzo che offriamo al venditore. È la voce fondamentale: da questa dipendono tasse di registro, imposta sostitutiva, ROI e utile netto.</p>
            <div className="grid grid-cols-3 gap-3">
              {SCENARIOS.map(s => (
                <div key={s.key}>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-blue-100 mb-1">{s.label}</div>
                  <input
                    type="number" step="0.01"
                    value={econ.proposta?.[s.key] ?? 0}
                    onChange={e => setTriple("proposta", s.key, e.target.value === "" ? 0 : Number(e.target.value))}
                    className="w-full h-11 rounded-lg bg-white/95 text-slate-900 font-mono-num font-bold text-lg px-3 focus:outline-none focus:ring-2 focus:ring-white/60"
                    data-testid={`piano-proposta-${s.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <ReverseCalculator econ={econ} setEcon={setEcon} />
        </div>
      </section>

      {/* Delta margine lordo istantaneo Vendita − Proposta per scenario */}
      <section data-testid="piano-delta-band">
        <div className="rounded-xl p-3 bg-gradient-to-r from-blue-50 via-slate-50 to-emerald-50 border border-slate-200 flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold shrink-0 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Margine lordo (vendita − proposta)
          </div>
          <div className="flex-1 grid grid-cols-3 gap-2">
            {SCENARIOS.map(s => {
              const delta = (Number(econ.ricavi?.[s.key]) || 0) - (Number(econ.proposta?.[s.key]) || 0);
              const positive = delta >= 0;
              return (
                <div key={s.key} className={`flex items-center justify-center gap-1.5 px-2 py-1 rounded-lg border ${positive ? "border-emerald-200 bg-white" : "border-rose-200 bg-white"}`} data-testid={`piano-delta-${s.key}`}>
                  <span className={`text-[9px] font-bold uppercase tracking-widest ${s.color}`}>{s.label}</span>
                  <span className={`text-base ${positive ? "text-emerald-600" : "text-rose-600"}`} aria-hidden="true">{positive ? "↑" : "↓"}</span>
                  <span className={`font-mono-num font-bold text-sm ${positive ? "text-emerald-700" : "text-rose-700"}`}>{fmtEuro(delta)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sezione 1-bis · Prezzo di vendita (hero card gemella della Proposta) */}
      <section>
        <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 text-white shadow-lg" data-testid="piano-ricavi-hero">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.24em] opacity-90">L'obiettivo di vendita</span>
          </div>
          <h3 className="font-display font-bold text-xl mb-1">Prezzo di vendita</h3>
          <p className="text-[11px] text-emerald-100 mb-4 max-w-2xl">Il prezzo a cui contiamo di rivendere l'immobile ristrutturato. Determina utile lordo, ROI e utile netto in ciascuno scenario.</p>
          <div className="grid grid-cols-3 gap-3">
            {SCENARIOS.map(s => (
              <div key={s.key}>
                <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-100 mb-1">{s.label}</div>
                <input
                  type="number" step="0.01"
                  value={econ.ricavi?.[s.key] ?? 0}
                  onChange={e => setTriple("ricavi", s.key, e.target.value === "" ? 0 : Number(e.target.value))}
                  className="w-full h-11 rounded-lg bg-white/95 text-slate-900 font-mono-num font-bold text-lg px-3 focus:outline-none focus:ring-2 focus:ring-white/60"
                  data-testid={`piano-ricavi-${s.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sezione 2 · Voci di costo (accordion) */}
      <section>
        {/* Il totale mostrato include la Manodopera (che è la voce a corpo del piano) */}
        <Accordion
          open={openCosts}
          onToggle={() => setOpenCosts(!openCosts)}
          title="Voci di costo"
          total={derived.costTot.medio + (Number(econ.manodopera?.medio) || 0)}
          testIdKey="costi"
          icon={<Receipt className="w-4 h-4 text-slate-700" strokeWidth={2.2} />}
          dotColor="bg-amber-500"
        />
        {openCosts && (
          <div className="border border-t-0 rounded-b-lg overflow-hidden -mt-1 pt-1">
            <table className="w-full text-xs">
              {HEAD}
              <tbody data-testid="piano-costi-detail">
                {/* Manodopera come prima riga, evidenziata perché è la voce a corpo del piano */}
                <tr className="bg-blue-50/50 border-b-2 border-blue-200" data-testid="piano-costi-manod-row">
                  <td className="px-2 py-1.5 font-semibold text-slate-800">Manodopera <span className="ml-1 text-[10px] text-blue-600 font-medium">(voce a corpo)</span></td>
                  {SCENARIOS.map(s => (
                    <td key={s.key} className="text-right px-2 py-1.5 font-mono-num font-semibold text-slate-800">{fmtEuro(econ.manodopera?.[s.key])}</td>
                  ))}
                  <td></td>
                </tr>
                {(econ.cost_lines || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-4 text-slate-400 text-[11px]">Nessuna voce aggiuntiva — aggiungile nel tab <b>Costi</b>.</td></tr>
                )}
                {(econ.cost_lines || []).map(l => {
                  const isPct = (l.mode || "fixed") === "percent";
                  const ref = isPct ? (PCT_REF_LABEL[l.pct_of] || "—") : "";
                  return (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 text-slate-700">
                        {l.label} {isPct && <span className="ml-1 text-[10px] text-purple-600 font-medium">({l.peggiore}% {ref})</span>}
                      </td>
                      {SCENARIOS.map(s => (
                        <td key={s.key} className="text-right px-2 py-1.5 font-mono-num text-slate-700">{fmtEuro(costLineAmount(l, econ, s.key))}</td>
                      ))}
                      <td></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Riga totale sempre visibile — include Manodopera */}
        <div className="border rounded-lg overflow-hidden mt-2">
          <table className="w-full text-xs">
            <tbody>
              <ScenarioRow
                label="Totale voci di costo (incl. Manodopera)"
                values={{
                  peggiore: derived.costTot.peggiore + (Number(econ.manodopera?.peggiore) || 0),
                  medio:    derived.costTot.medio    + (Number(econ.manodopera?.medio) || 0),
                  migliore: derived.costTot.migliore + (Number(econ.manodopera?.migliore) || 0),
                }}
                boldTotal
                rowBg="bg-amber-50/40"
              />
            </tbody>
          </table>
        </div>
      </section>

      {/* Sezione 3 · Materiali (accordion) */}
      <section>
        <Accordion
          open={openMats}
          onToggle={() => setOpenMats(!openMats)}
          title="Materiali"
          total={derived.matTot}
          testIdKey="materiali"
          icon={<Layers className="w-4 h-4 text-slate-700" strokeWidth={2.2} />}
          dotColor="bg-purple-500"
        />
        {openMats && (
          <div className="border border-t-0 rounded-b-lg overflow-hidden -mt-1 pt-1">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-1.5 uppercase tracking-wider text-[10px] text-slate-500">Voce</th>
                  <th className="text-right px-2 py-1.5 uppercase tracking-wider text-[10px] text-slate-500 w-20">Qtà</th>
                  <th className="text-right px-2 py-1.5 uppercase tracking-wider text-[10px] text-slate-500 w-28">€/unità</th>
                  <th className="text-right px-2 py-1.5 uppercase tracking-wider text-[10px] text-slate-500 w-28">Totale</th>
                </tr>
              </thead>
              <tbody data-testid="piano-materiali-detail">
                {(econ.materials || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center py-4 text-slate-400 text-[11px]">Nessun materiale — aggiungili nel tab <b>Materiali</b>.</td></tr>
                )}
                {(econ.materials || []).map(m => {
                  const q = matQty(m); const p = matPrezzo(m);
                  return (
                    <tr key={m.id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 text-slate-700">{m.label}</td>
                      <td className="text-right px-2 py-1.5 font-mono-num text-slate-700">{fmtNum(q, 2)}</td>
                      <td className="text-right px-2 py-1.5 font-mono-num text-slate-700">{fmtEuro(p)}</td>
                      <td className="text-right px-2 py-1.5 font-mono-num text-slate-800 font-semibold">{fmtEuro(q * p)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="border rounded-lg overflow-hidden mt-2">
          <table className="w-full text-xs">
            <tbody>
              <ScenarioRow label="Totale materiali" values={{ peggiore: derived.matTot, medio: derived.matTot, migliore: derived.matTot }} boldTotal rowBg="bg-purple-50/40" />
            </tbody>
          </table>
        </div>
      </section>

      {/* Sezione 4 · Calcoli automatici */}
      <section>
        <SectionHeader icon={Landmark} title="Calcoli automatici" dotColor="bg-orange-500" testId="piano-sec-auto" />
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            {HEAD}
            <tbody>
              <tr className="bg-amber-50/40">
                <td className="px-2 py-1.5 text-slate-700 flex items-center gap-1">
                  Imprevisti ·
                  <NumCell value={econ.imprevisti_pct} onChange={v => setEcon(s => ({ ...s, imprevisti_pct: Number(v) || 0 }))} className="w-14 h-6" testId="piano-imprevisti-pct" />
                  <span className="text-[10px] text-slate-500">% manodopera</span>
                </td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className="text-right px-2 py-1.5 font-mono-num text-slate-700">
                    {fmtEuro((Number(econ.manodopera?.[s.key]) || 0) * (Number(econ.imprevisti_pct) || 0) / 100)}
                  </td>
                ))}
                <td></td>
              </tr>
              <tr className="bg-amber-50/40">
                <td className="px-2 py-1.5 text-slate-700">
                  Tasse registro ({econ.tipo_acquirente === "srl" ? "9% proposta" : econ.tipo_acquirente === "2casa" ? "rendita × 126 × 9%" : "rendita × 115,5 × 2%"})
                </td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className="text-right px-2 py-1.5 font-mono-num text-slate-700">{fmtEuro(derived.tasse)}</td>
                ))}
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs mt-2">
          <div className="flex items-center gap-2">
            <span className="text-slate-600 font-semibold">Rendita catastale (€):</span>
            <NumCell value={econ.rendita_catastale} onChange={v => setEcon(s => ({ ...s, rendita_catastale: Number(v) || 0 }))} className="w-28" testId="piano-rendita" />
          </div>
          <div className="text-slate-500">Base 1°casa: <b className="text-slate-800 font-mono-num">{fmtEuro((Number(econ.rendita_catastale) || 0) * 115.5)}</b></div>
          <div className="text-slate-500">Base 2°casa: <b className="text-slate-800 font-mono-num">{fmtEuro((Number(econ.rendita_catastale) || 0) * 126)}</b></div>
        </div>
      </section>

      {/* Sezione 5 · Tot Spese */}
      <section>
        <div className="border-2 border-slate-300 rounded-xl overflow-hidden bg-gradient-to-r from-slate-100 to-slate-50">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="px-3 py-3 font-display font-bold uppercase tracking-[0.12em] text-slate-900">TOT. Spese + Acquisto</td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className={`text-right px-3 py-3 font-mono-num font-bold text-base ${SCENARIOS.find(x=>x.key===s.key).color}`} data-testid={`piano-tot-${s.key}`} style={{ width: 130 }}>
                    {fmtEuro(derived.totSpese[s.key])}
                  </td>
                ))}
                <td style={{ width: 32 }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Sezione 6 · Ricavi & margini */}
      <section>
        <SectionHeader icon={TrendingUp} title="Ricavi & margini" dotColor="bg-emerald-500" testId="piano-sec-ricavi" />
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            {HEAD}
            <tbody>
              <tr className="bg-emerald-50/40">
                <td className="px-2 py-1.5 text-slate-700 flex items-center gap-1">
                  Prezzo pubblicità (auto · +
                  <NumCell value={econ.pubblicita_pct} onChange={v => setEcon(s => ({ ...s, pubblicita_pct: Number(v) || 0 }))} className="w-14 h-6" testId="piano-pub-pct" />
                  <span className="text-[10px] text-slate-500">%)</span>
                </td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className="text-right px-2 py-1.5 font-mono-num text-slate-700">{fmtEuro(derived.pubblicita[s.key])}</td>
                ))}
                <td></td>
              </tr>
              <tr className="bg-slate-100 border-t-2 border-slate-300">
                <td className="px-2 py-2 font-bold text-slate-900">Utile Lordo</td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className={`text-right px-2 py-2 font-mono-num font-bold ${derived.utileLordo[s.key] >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`piano-utile-${s.key}`}>
                    {fmtEuro(derived.utileLordo[s.key])}
                  </td>
                ))}
                <td></td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 text-slate-700">ROI (utile / spese)</td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className="text-right px-2 py-1.5 font-mono-num text-slate-800" data-testid={`piano-roi-${s.key}`}>{fmtPct(derived.roi[s.key])}</td>
                ))}
                <td></td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 text-slate-700 flex items-center gap-1">
                  ROE (utile / esposizione ·
                  <NumCell value={econ.esposizione} onChange={v => setEcon(s => ({ ...s, esposizione: Number(v) || 0 }))} className="w-24 h-6" testId="piano-esposizione" />
                  <span className="text-[10px] text-slate-500">€)</span>
                </td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className="text-right px-2 py-1.5 font-mono-num text-slate-800" data-testid={`piano-roe-${s.key}`}>{fmtPct(derived.roe[s.key])}</td>
                ))}
                <td></td>
              </tr>
              <tr className="bg-slate-50">
                <td className="px-2 py-1.5 text-slate-700 flex items-center gap-1">
                  Utile netto (senza residenza · imposta
                  <NumCell value={econ.sostitutiva_pct} onChange={v => setEcon(s => ({ ...s, sostitutiva_pct: Number(v) || 0 }))} className="w-14 h-6" testId="piano-sost-pct" />
                  <span className="text-[10px] text-slate-500">%)</span>
                </td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className={`text-right px-2 py-1.5 font-mono-num font-semibold ${derived.utileNettoSenzaResid[s.key] >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {fmtEuro(derived.utileNettoSenzaResid[s.key])}
                  </td>
                ))}
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// TAB 2 · Costi (Manodopera a corpo + Voci di costo)
// ─────────────────────────────────────────────────────────────────────
const CostiTab = ({ econ, setEcon, property }) => {
  const setTriple = (field, k, v) => setEcon(s => ({ ...s, [field]: { ...(s[field] || emptyTriple()), [k]: Number(v) || 0 } }));
  const applyRenoHint = () => {
    const mq = Number(property?.mq) || 0;
    const hint = RENO_HINTS[econ.reno_scenario || "LIGHT"];
    if (!mq) return toast.warning("mq immobile mancanti — impossibile applicare l'auto-fill");
    setEcon(s => ({ ...s, manodopera: { peggiore: hint.peggiore * mq, medio: hint.medio * mq, migliore: hint.migliore * mq } }));
    toast.success(`Manodopera aggiornata da ${econ.reno_scenario}`);
  };
  const renoDesc = RENO_HINTS[econ.reno_scenario || "LIGHT"]?.desc;

  const updLine = (id, k, v) => setEcon(s => ({
    ...s,
    cost_lines: (s.cost_lines || []).map(l => l.id === id ? {
      ...l,
      [k]: (k === "label" || k === "note" || k === "mode" || k === "pct_of") ? v : Number(v) || 0,
    } : l),
  }));
  const addLine = () => setEcon(s => ({
    ...s,
    cost_lines: [...(s.cost_lines || []), { id: uid("cl"), label: "Nuova voce", peggiore: 0, medio: 0, migliore: 0, note: "", mode: "fixed", pct_of: "" }],
  }));
  const rmLine = (id) => setEcon(s => ({ ...s, cost_lines: (s.cost_lines || []).filter(l => l.id !== id) }));

  return (
    <div className="space-y-6">
      {/* Manodopera — voce a corpo, evidenziata come blocco singolo */}
      <section data-testid="piano-manod-section">
        <SectionHeader
          icon={Wand2}
          title="Manodopera — voce a corpo"
          dotColor="bg-blue-500"
          testId="piano-manod-header"
          right={
            <div className="flex items-center gap-2">
              <Select value={econ.reno_scenario || "LIGHT"} onValueChange={v => setEcon(s => ({ ...s, reno_scenario: v }))}>
                <SelectTrigger className="h-7 w-28 text-xs" data-testid="piano-reno-scenario"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LIGHT">LIGHT</SelectItem>
                  <SelectItem value="MEDIUM">MEDIUM</SelectItem>
                  <SelectItem value="STRONG">STRONG</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-7 rounded-full" onClick={applyRenoHint} data-testid="piano-apply-reno">
                <Wand2 className="w-3 h-3 mr-1" /> Auto-fill
              </Button>
            </div>
          }
        />
        <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-white">
          <table className="w-full text-xs">
            <thead className="bg-blue-100/60">
              <tr>
                <th className="text-left px-3 py-2 uppercase tracking-wider text-[10px] text-blue-800 font-bold">Voce</th>
                {SCENARIOS.map(s => (
                  <th key={s.key} className={`text-right px-3 py-2 uppercase tracking-wider text-[10px] font-bold ${s.color}`} style={{ width: 140 }}>{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3 py-3 font-semibold text-slate-900">
                  Manodopera
                  <div className="text-[10px] text-slate-500 font-normal mt-0.5">Somma unica lavori edili + impianti — usare Auto-fill per stimare da m² × tipologia</div>
                </td>
                {SCENARIOS.map(s => (
                  <td key={s.key} className="px-2 py-3">
                    <input
                      type="number" step="0.01"
                      value={econ.manodopera?.[s.key] ?? 0}
                      onChange={e => setTriple("manodopera", s.key, e.target.value === "" ? 0 : Number(e.target.value))}
                      className="w-full h-10 rounded-md border border-blue-200 bg-white text-right font-mono-num font-bold text-sm px-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid={`piano-manod-${s.key}`}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-400 italic mt-1 pl-1">{renoDesc}</div>
      </section>

      {/* Voci di costo */}
      <section>
        <SectionHeader icon={Receipt} title="Voci di costo" dotColor="bg-amber-500" testId="piano-costi-header" />
        <p className="text-xs text-slate-600 -mt-1 mb-2">
          Ogni voce può essere in <b>€</b> (importo fisso per scenario) oppure in <b>%</b> di un campo del piano (Ricavi / Proposta / Manodopera / Rendita).
        </p>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">Voce</th>
                <th className="text-left px-2 py-2 w-16">Tipo</th>
                <th className="text-left px-2 py-2 w-36">Riferimento %</th>
                <th className="text-right px-2 py-2 w-24">Peggiore</th>
                <th className="text-right px-2 py-2 w-24">Medio</th>
                <th className="text-right px-2 py-2 w-24">Migliore</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(econ.cost_lines || []).map(l => {
                const isPct = (l.mode || "fixed") === "percent";
                return (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-1 py-1"><Input value={l.label} onChange={e => updLine(l.id, "label", e.target.value)} className="h-8 text-xs" data-testid={`piano-line-label-${l.id}`} /></td>
                    <td className="px-1 py-1">
                      <Select value={l.mode || "fixed"} onValueChange={v => updLine(l.id, "mode", v)}>
                        <SelectTrigger className="h-8 text-xs px-2" data-testid={`piano-line-mode-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">€</SelectItem>
                          <SelectItem value="percent">%</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-1 py-1">
                      {isPct ? (
                        <Select value={l.pct_of || ""} onValueChange={v => updLine(l.id, "pct_of", v)}>
                          <SelectTrigger className="h-8 text-xs px-2" data-testid={`piano-line-pctof-${l.id}`}><SelectValue placeholder="di…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ricavi">Ricavi</SelectItem>
                            <SelectItem value="proposta">Proposta</SelectItem>
                            <SelectItem value="manodopera">Manodopera</SelectItem>
                            <SelectItem value="rendita">Rendita catastale</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[10px] text-slate-300 pl-2">—</span>
                      )}
                    </td>
                    {SCENARIOS.map(s => (
                      <td key={s.key} className="px-1 py-1">
                        <div className="relative">
                          <NumCell value={l[s.key]} onChange={v => updLine(l.id, s.key, v)} testId={`piano-line-${s.key}-${l.id}`} className={isPct ? "pr-5" : ""} />
                          {isPct && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none font-mono-num">%</span>}
                        </div>
                      </td>
                    ))}
                    <td className="px-1 py-1"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" onClick={() => rmLine(l.id)} data-testid={`piano-line-del-${l.id}`}><Trash2 className="w-3 h-3" /></Button></td>
                  </tr>
                );
              })}
              {(econ.cost_lines || []).length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">Nessuna voce di costo. Aggiungi la prima.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" className="rounded-full mt-2" onClick={addLine} data-testid="piano-line-add">
          <Plus className="w-3 h-3 mr-1" /> Aggiungi voce di costo
        </Button>
      </section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// TAB 3 · Materiali
// ─────────────────────────────────────────────────────────────────────
const MaterialiTab = ({ econ, setEcon, derived }) => {
  const upd = (id, k, v) => setEcon(s => ({
    ...s,
    materials: (s.materials || []).map(m => m.id === id ? { ...m, [k]: k === "label" ? v : Number(v) || 0 } : m),
  }));
  const add = () => setEcon(s => ({
    ...s,
    materials: [...(s.materials || []), { id: uid("mt"), label: "Nuova voce", quantita: 1, prezzo: 0 }],
  }));
  const rm = (id) => setEcon(s => ({ ...s, materials: (s.materials || []).filter(m => m.id !== id) }));

  return (
    <div className="space-y-4">
      <SectionHeader icon={Layers} title="Materiali del piano" dotColor="bg-purple-500" testId="piano-materiali-header" />
      <p className="text-xs text-slate-600 -mt-1">
        <b>Quantità × €/unità</b> = totale. La somma dei totali confluisce nella scheda <b>Scenari</b>.
      </p>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-2 py-2">Voce materiali</th>
              <th className="text-right px-2 py-2 w-20">Qtà</th>
              <th className="text-right px-2 py-2 w-28">€/unità</th>
              <th className="text-right px-2 py-2 w-32">Totale</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {(econ.materials || []).map(m => {
              const q = matQty(m); const p = matPrezzo(m); const t = q * p;
              return (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-1 py-1"><Input value={m.label} onChange={e => upd(m.id, "label", e.target.value)} className="h-8 text-xs" data-testid={`piano-mat-label-${m.id}`} /></td>
                  <td className="px-1 py-1"><NumCell value={q} onChange={v => upd(m.id, "quantita", v)} testId={`piano-mat-qty-${m.id}`} /></td>
                  <td className="px-1 py-1"><NumCell value={p} onChange={v => upd(m.id, "prezzo", v)} testId={`piano-mat-prezzo-${m.id}`} /></td>
                  <td className="px-2 py-1 text-right font-mono-num text-slate-800 font-semibold" data-testid={`piano-mat-tot-${m.id}`}>{fmtEuro(t)}</td>
                  <td className="px-1 py-1"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" onClick={() => rm(m.id)} data-testid={`piano-mat-del-${m.id}`}><Trash2 className="w-3 h-3" /></Button></td>
                </tr>
              );
            })}
            {(econ.materials || []).length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400">Nessun materiale. Aggiungi la prima voce.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 border-t-2 border-slate-300">
              <td colSpan={3} className="px-2 py-2 font-bold text-slate-900">TOTALE MATERIALI</td>
              <td className="text-right px-2 py-2 font-mono-num font-bold text-slate-900" data-testid="piano-mat-tot-all">{fmtEuro(derived.matTot)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Button variant="outline" size="sm" className="rounded-full" onClick={add} data-testid="piano-mat-add"><Plus className="w-3 h-3 mr-1" /> Aggiungi materiale</Button>
    </div>
  );
};

// KPI panel laterale
const KPIPanel = ({ derived, econ, setEcon, plan }) => {
  const azzera = () => {
    if (!window.confirm("Azzerare tutte le voci di costo, materiali, manodopera, tasse e portare Proposta = Ricavi = Prezzo al pubblico? Tutti gli scenari risulteranno a 0.")) return;
    const prezzo = Number(plan?.property?.prezzo) || 0;
    setEcon(s => ({
      ...s,
      proposta:   { peggiore: prezzo, medio: prezzo, migliore: prezzo },
      manodopera: { peggiore: 0, medio: 0, migliore: 0 },
      ricavi:     { peggiore: prezzo, medio: prezzo, migliore: prezzo },
      rendita_catastale: 0,
      cost_lines: (s.cost_lines || []).map(l => ({ ...l, peggiore: 0, medio: 0, migliore: 0 })),
      materials:  (s.materials  || []).map(m => ({ ...m, prezzo: 0, prezzo_a: 0, prezzo_b: 0 })),
    }));
    toast.success("Piano azzerato — tutti gli scenari a 0");
  };

  const duplicaScenario = (srcKey) => {
    const srcLabel = SCENARIOS.find(x => x.key === srcKey)?.label || srcKey;
    if (!window.confirm(`Copiare i valori dello scenario ${srcLabel} sugli altri due (Proposta, Manodopera, Ricavi e Voci di costo)?`)) return;
    setEcon(s => {
      const copy3 = (triple) => {
        const v = Number((triple || {})[srcKey]) || 0;
        return { peggiore: v, medio: v, migliore: v };
      };
      return {
        ...s,
        proposta:   copy3(s.proposta),
        manodopera: copy3(s.manodopera),
        ricavi:     copy3(s.ricavi),
        cost_lines: (s.cost_lines || []).map(l => {
          const v = Number(l[srcKey]) || 0;
          return { ...l, peggiore: v, medio: v, migliore: v };
        }),
      };
    });
    toast.success(`Scenari copiati da ${srcLabel}`);
  };

  return (
    <aside className="ios-card p-4 space-y-3 bg-gradient-to-br from-slate-50 to-white h-fit sticky top-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
        <Coins className="w-3 h-3" /> Riepilogo scenari
      </div>
      {SCENARIOS.map(s => {
        const u = derived.utileLordo[s.key];
        const r = derived.roi[s.key];
        return (
          <div key={s.key} className={`rounded-lg p-2 ${s.bg} relative`} data-testid={`piano-kpi-scen-${s.key}`}>
            <div className="flex items-center justify-between gap-2">
              <div className={`text-[10px] font-bold uppercase tracking-wider ${s.color}`}>{s.label}</div>
              <button
                type="button"
                onClick={() => duplicaScenario(s.key)}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 hover:text-slate-900 bg-white/70 hover:bg-white border border-slate-200 hover:border-slate-300 shadow-sm rounded-full px-2 py-0.5 transition-colors"
                title={`Copia i valori di ${s.label} sugli altri scenari`}
                data-testid={`piano-dup-${s.key}`}
              >
                <Copy className="w-3 h-3" /> Duplica
              </button>
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-[10px] text-slate-500">Utile</span>
              <span className={`font-mono-num font-bold text-sm ${u >= 0 ? "text-slate-800" : "text-rose-700"}`}>{fmtEuro(u)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-slate-500">ROI</span>
              <span className={`font-mono-num text-xs ${r >= 20 ? "text-emerald-700" : r >= 10 ? "text-amber-700" : "text-rose-700"}`}>{fmtPct(r)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-slate-500">Utile netto</span>
              <span className="font-mono-num text-xs text-slate-700">{fmtEuro(derived.utileNettoSenzaResid[s.key])}</span>
            </div>
          </div>
        );
      })}
      <div className="pt-2 border-t space-y-1 text-[11px]" data-testid="piano-kpi-medio">
        <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Snapshot scenario medio</div>
        <div className="flex justify-between"><span className="text-slate-500">Tot. voci di costo</span><span className="font-mono-num text-slate-800" data-testid="piano-kpi-costtot">{fmtEuro(derived.costTot.medio + (Number(econ?.manodopera?.medio) || 0))}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Tot. materiali</span><span className="font-mono-num text-slate-800" data-testid="piano-kpi-mattot">{fmtEuro(derived.matTot)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Tasse registro</span><span className="font-mono-num text-slate-800" data-testid="piano-kpi-tasse">{fmtEuro(derived.tasse)}</span></div>
      </div>
      <Button
        variant="outline" size="sm" onClick={azzera}
        className="w-full rounded-full text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 mt-2"
        data-testid="piano-azzera"
      >
        <RotateCcw className="w-3 h-3 mr-1" /> Azzera piano
      </Button>
    </aside>
  );
};

// Dialog principale
const EconomicPlanEditor = ({ open, onOpenChange, plan, onSaved }) => {
  const [econ, setEcon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("scenari");

  useEffect(() => {
    if (!plan) return;
    setEcon(JSON.parse(JSON.stringify(plan.economic || {
      rendita_catastale: 0, esposizione: 0, tipo_acquirente: "1casa", reno_scenario: "LIGHT",
      imprevisti_pct: 15, sostitutiva_pct: 26, pubblicita_pct: 10, street_discount_pct: 15,
      proposta: emptyTriple(), manodopera: emptyTriple(), ricavi: emptyTriple(),
      cost_lines: [], materials: [],
    })));
    setTab("scenari");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.property_id, open]);

  const derived = useMemo(() => econ ? computeDerived(econ, plan?.property) : null, [econ, plan?.property]);

  const save = useCallback(async () => {
    if (!plan || !econ) return;
    setSaving(true);
    try {
      await api.put(`/piano/plans/${plan.property_id}/economic`, { economic: econ });
      toast.success("Piano economico salvato");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  }, [plan, econ, onSaved, onOpenChange]);

  const reset = useCallback(async () => {
    if (!plan) return;
    if (!window.confirm("Ripristinare i preset di default? Le modifiche non salvate andranno perse.")) return;
    try {
      const r = await api.post(`/piano/plans/${plan.property_id}/economic/reset`);
      setEcon(r.data.economic);
      toast.success("Preset ripristinati");
      onSaved?.();
    } catch { toast.error("Errore ripristino"); }
  }, [plan, onSaved]);

  const doExportPdf = useCallback(() => {
    if (!plan || !econ || !derived) return;
    try { exportPianoPdf({ plan, econ, derived }); toast.success("PDF generato"); }
    catch (e) { console.error("[piano] pdf export failed:", e); toast.error("Errore export PDF"); }
  }, [plan, econ, derived]);

  if (!plan || !econ || !derived) return null;
  const p = plan.property || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] max-h-[92vh] overflow-hidden flex flex-col" data-testid="piano-editor">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl flex items-center gap-2">
            <Home className="w-5 h-5 text-[#0A84FF]" />
            Piano Economico · {p.indirizzo || "—"} {p.civico || ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Editor Piano Economico con tab Scenari, Costi e Materiali.
          </DialogDescription>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>{p.citta}{p.macro_area ? ` · ${p.macro_area}` : ""}</span>
            <span>Sup: <b className="text-slate-800">{fmtNum(p.mq)} m²</b></span>
            <span>Prezzo pubbl.: <b className="text-slate-800">{fmtEuro(p.prezzo)}</b></span>
            <span>€/m²: <b className="text-slate-800">{fmtEuro(p.eur_mq)}</b></span>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4 overflow-hidden flex-1">
          <div className="overflow-y-auto pr-2">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="scenari"   data-testid="piano-tab-scenari"><Percent className="w-3 h-3 mr-1" />Scenari</TabsTrigger>
                <TabsTrigger value="costi"     data-testid="piano-tab-costi"><Receipt className="w-3 h-3 mr-1" />Costi</TabsTrigger>
                <TabsTrigger value="materiali" data-testid="piano-tab-mat"><Layers className="w-3 h-3 mr-1" />Materiali</TabsTrigger>
              </TabsList>
              <TabsContent value="scenari" className="mt-4">
                <ScenariiTab econ={econ} setEcon={setEcon} derived={derived} property={p} />
              </TabsContent>
              <TabsContent value="costi" className="mt-4">
                <CostiTab econ={econ} setEcon={setEcon} property={p} />
              </TabsContent>
              <TabsContent value="materiali" className="mt-4">
                <MaterialiTab econ={econ} setEcon={setEcon} derived={derived} />
              </TabsContent>
            </Tabs>
          </div>
          <KPIPanel derived={derived} econ={econ} setEcon={setEcon} plan={plan} />
        </div>

        <DialogFooter className="mt-2 gap-2 flex-wrap">
          <Button variant="outline" onClick={reset} className="rounded-full" data-testid="piano-reset"><RotateCcw className="w-4 h-4 mr-1" /> Ripristina preset</Button>
          <Button variant="outline" onClick={doExportPdf} className="rounded-full" data-testid="piano-export-pdf"><FileDown className="w-4 h-4 mr-1" /> Export PDF</Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0A84FF] rounded-full" data-testid="piano-save">
            {saving ? "Salvataggio…" : "Salva piano"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EconomicPlanEditor;

